import { CalendarIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../../ui/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../ui/components/ui/popover';
import { FilterMetaYearResponseDto } from '../../types/response';

interface KpiWeekSelectorProps {
  years: FilterMetaYearResponseDto[];
  currentYear: number;
  currentWeek: number;
  disabled?: boolean;
  onChange: (week: string, year: string) => void;
}

const KpiWeekSelector = ({ years, currentYear, currentWeek, disabled, onChange }: KpiWeekSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [pendingYear, setPendingYear] = useState<number>(currentYear);
  const [pendingWeek, setPendingWeek] = useState<number | null>(currentWeek);

  const activeMonthRef = useRef<HTMLDivElement>(null);

  const selectedYearData = years.find((y) => y.year === pendingYear);
  const months = selectedYearData?.months ?? [];
  const canConfirm = pendingWeek !== null;

  useEffect(() => {
    setPendingYear(currentYear);
    setPendingWeek(currentWeek);
  }, [currentYear, currentWeek]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        activeMonthRef.current?.scrollIntoView({ block: 'nearest' });
      }, 0);
    }
  }, [open]);

  const handleYearSelect = (year: number) => {
    setPendingYear(year);
    setPendingWeek(year === currentYear ? currentWeek : null);
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onChange(String(pendingWeek), String(pendingYear));
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    setPendingYear(currentYear);
    setPendingWeek(currentWeek);
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className="gap-2">
          <CalendarIcon className="h-4 w-4" />
          {currentYear} – KW {currentWeek}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-4 space-y-4" align="start">
        {/* Year */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Jahr</p>
          <div className="flex flex-wrap gap-1.5">
            {years.map(({ year }) => (
              <Button
                key={year}
                size="sm"
                variant={pendingYear === year ? 'default' : 'outline'}
                className="h-7 px-2.5 text-xs"
                onClick={() => handleYearSelect(year)}>
                {year}
              </Button>
            ))}
          </div>
        </div>

        {/* Weeks grouped by month */}
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground">
            Kalenderwoche
            {!pendingWeek && <span className="ml-1 text-destructive">– bitte wählen</span>}
          </p>

          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {months.map(({ month, title, weeks }) => {
              const isActiveMonth = weeks.includes(pendingWeek ?? currentWeek);

              return (
                <div key={month} ref={isActiveMonth ? activeMonthRef : null}>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">{title}</p>
                  <div className="flex flex-wrap gap-1">
                    {weeks.map((week) => (
                      <Button
                        key={week}
                        size="sm"
                        variant={pendingWeek === week ? 'default' : 'ghost'}
                        className="h-7 px-2 text-xs"
                        onClick={() => setPendingWeek(week)}>
                        KW {week}
                      </Button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Confirm */}
        <Button className="w-full" size="sm" disabled={!canConfirm} onClick={handleConfirm}>
          Übernehmen
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export default KpiWeekSelector;
